import { copy } from "../copy";
import Icon from "../components/Icon";
import type { Review } from "./api";
import { Avatar, timeAgo } from "./ui";

function Stars({ value }: { value: number }) {
  return (
    <span role="img" aria-label={copy.contest.reviews.stars(value)} className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <Icon key={n} name={n <= value ? "star" : "starLine"} className={`h-3.5 w-3.5 ${n <= value ? "text-gold" : "text-chrome-dim/50"}`} />
      ))}
    </span>
  );
}

const mediaLink =
  "bf-focusable inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.14em] text-gold hover:text-gold-light";

// Per-track reviews, read-only. Writing reviews happens elsewhere.
export default function ReviewsTab({ reviews }: { reviews: Review[] }) {
  const r = copy.contest.reviews;
  const average = reviews.reduce((sum, v) => sum + v.rating, 0) / reviews.length;
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-3 rounded-2xl border border-ground-edge bg-ground-raised p-5">
        <div>
          <p className="bf-tabular font-headline text-4xl leading-none text-gold">{average.toFixed(1)}</p>
          <p className="mt-1 text-[10px] uppercase tracking-[0.18em] text-chrome-dim">{r.average}</p>
        </div>
        <div className="border-l border-ground-edge pl-6">
          <Stars value={Math.round(average)} />
          <p className="mt-1.5 text-[11px] uppercase tracking-[0.14em] text-chrome-dim">{r.count(reviews.length)}</p>
        </div>
      </div>

      <ul data-testid="review-list" className="space-y-5">
        {reviews.map((review, i) => (
          <li key={`${review.at}-${review.reviewer_username}-${i}`} className="flex gap-3.5">
            <Avatar username={review.reviewer_username} logoUrl={review.reviewer_logo_url} />
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
                <span className="text-[13px] font-bold text-chrome">@{review.reviewer_username}</span>
                <Stars value={review.rating} />
                <span className="text-[11px] uppercase tracking-[0.12em] text-chrome-dim">{timeAgo(review.at)}</span>
              </div>
              {(review.track_title || review.producer_username) && (
                <p className="mt-1 text-[11px] uppercase tracking-[0.12em] text-chrome-dim">
                  {review.track_title && r.on(review.track_title)} {review.producer_username && r.by(review.producer_username)}
                </p>
              )}
              {review.body && <p className="mt-1.5 break-words text-[13px] leading-relaxed text-chrome-dim">{review.body}</p>}
              {(review.video_url || review.audio_url) && (
                <div className="mt-2 flex flex-wrap gap-4">
                  {review.video_url && (
                    <a href={review.video_url} target="_blank" rel="noopener noreferrer" className={mediaLink}>
                      <Icon name="video" className="h-3.5 w-3.5" />
                      {r.watch}
                    </a>
                  )}
                  {review.audio_url && (
                    <a href={review.audio_url} target="_blank" rel="noopener noreferrer" className={mediaLink}>
                      <Icon name="headphone" className="h-3.5 w-3.5" />
                      {r.listen}
                    </a>
                  )}
                </div>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
